import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Calendar,
  Users,
  Eye,
  Search,
  Filter,
  RefreshCw,
  MapPin,
  Clock,
  CheckCircle,
  XCircle,
  Star,
  CalendarDays,
  Activity,
  BookOpen,
  TrendingUp
} from 'lucide-react';
import { supabase } from '../../utils/supabaseClient';
import '../../styles/CuratorEvents.css';

const CuratorEvents = () => {
  const navigate = useNavigate();
  
  // State management
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [stats, setStats] = useState({
    total: 0,
    upcoming: 0,
    past: 0,
    attendees: 0
  });

  // Get current user
  const getCurrentUser = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    return user;
  };

  // Load events for curator
  const loadCuratorEvents = async () => {
    try {
      setLoading(true);
      setError(null);

      const user = await getCurrentUser();
      if (!user) {
        throw new Error('User not authenticated');
      }

      // Get curator's gallery access
      const { data: galleryAccess, error: accessError } = await supabase
        .from('gallery_access')
        .select('gallery_id')
        .eq('user_uuid', user.id);

      if (accessError) throw accessError;
      
      if (!galleryAccess || galleryAccess.length === 0) {
        console.log('No gallery access found for curator');
        setEvents([]);
        setStats({ total: 0, upcoming: 0, past: 0, attendees: 0 });
        return;
      }

      const galleryIds = galleryAccess.map(access => access.gallery_id);
      console.log('Curator has access to galleries:', galleryIds);

      // Get collections from curator's galleries
      const { data: collectionMappings, error: collectionsError } = await supabase
        .from('gallery_collection_mapping')
        .select('collection_id')
        .in('gallery_id', galleryIds);

      if (collectionsError) {
        console.warn('Error fetching collections:', collectionsError);
      }

      const collectionIds = collectionMappings?.map(mapping => mapping.collection_id) || [];
      console.log('Collections accessible to curator:', collectionIds);

      // Build query for events - show events where gallery_id OR collection_id matches curator's access
      let eventsQuery = supabase
        .from('events')
        .select(`
          id,
          name,
          event_start,
          event_end,
          eventbrite_id,
          status,
          created_at,
          updated_at,
          collection_id,
          gallery_id,
          galleries!gallery_id (
            name,
            address,
            type
          ),
          collections!collection_id (
            title,
            description
          ),
          artspeaks_stories!event_id (
            story_id,
            title,
            featured
          )
        `)
        .order('event_start', { ascending: false });

      // Filter by gallery_id OR collection_id
      if (collectionIds.length > 0) {
        eventsQuery = eventsQuery.or(`gallery_id.in.(${galleryIds.join(',')}),collection_id.in.(${collectionIds.join(',')})`);
      } else {
        eventsQuery = eventsQuery.in('gallery_id', galleryIds);
      }

      // Add search filter
      if (searchQuery && searchQuery.trim() !== '') {
        eventsQuery = eventsQuery.ilike('name', `%${searchQuery}%`);
      }

      // Add status filter
      if (statusFilter !== 'all') {
        if (statusFilter === 'upcoming') {
          eventsQuery = eventsQuery.gte('event_start', new Date().toISOString());
        } else if (statusFilter === 'past') {
          eventsQuery = eventsQuery.lt('event_end', new Date().toISOString());
        } else if (statusFilter === 'active') {
          const now = new Date().toISOString();
          eventsQuery = eventsQuery.lte('event_start', now).gte('event_end', now);
        }
      }

      const { data: eventsData, error: eventsError } = await eventsQuery;
      if (eventsError) throw eventsError;

      if (!eventsData || eventsData.length === 0) {
        console.log('No events found for curator');
        setEvents([]);
        setStats({ total: 0, upcoming: 0, past: 0, attendees: 0 });
        return;
      }

      console.log('Found events:', eventsData.length);

      // Enrich events with additional data
      const enrichedEvents = await Promise.all(eventsData.map(async (event) => {
        // Get attendee count if eventbrite_id exists
        let attendeeCount = 0;
        let checkedInCount = 0;
        
        if (event.eventbrite_id) {
          try {
            // Use the RPC function to get attendee data
            const { data: attendeeData, error: attendeeError } = await supabase
              .rpc('event_attendees', { event_eventbrite_id: event.eventbrite_id });
            
            if (!attendeeError && attendeeData) {
              attendeeCount = attendeeData.length;
              checkedInCount = attendeeData.filter(attendee => attendee.checked_in).length;
            }
          } catch (err) {
            console.warn('Error fetching attendee data for event:', event.id, err);
          }
        }

        // Determine event status
        const now = new Date();
        const startDate = new Date(event.event_start);
        const endDate = new Date(event.event_end);
        
        let eventStatus = 'upcoming';
        if (endDate < now) {
          eventStatus = 'past';
        } else if (startDate <= now && endDate >= now) {
          eventStatus = 'active';
        }

        return {
          ...event,
          attendee_count: attendeeCount,
          checked_in_count: checkedInCount,
          story_count: event.artspeaks_stories?.length || 0,
          featured_stories: event.artspeaks_stories?.filter(story => story.featured).length || 0,
          event_status: eventStatus,
          gallery_name: event.galleries?.name || 'Unknown Gallery',
          collection_title: event.collections?.title || null
        };
      }));

      setEvents(enrichedEvents);

      // Calculate stats
      const now = new Date();
      const totalEvents = enrichedEvents.length;
      const upcomingEvents = enrichedEvents.filter(e => new Date(e.event_start) > now).length;
      const pastEvents = enrichedEvents.filter(e => new Date(e.event_end) < now).length;
      const totalAttendees = enrichedEvents.reduce((sum, e) => sum + e.attendee_count, 0);

      setStats({
        total: totalEvents,
        upcoming: upcomingEvents,
        past: pastEvents,
        attendees: totalAttendees
      });

    } catch (err) {
      console.error('Error loading curator events:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Load events on mount and when filters change
  useEffect(() => {
    loadCuratorEvents();
  }, [searchQuery, statusFilter]);

  // Handle event click - navigate to curator event detail
  const handleEventClick = (eventId) => {
    navigate(`/curator/events/${eventId}`);
  };

  // Filter events based on current filters
  const filteredEvents = events.filter(event => {
    const matchesSearch = !searchQuery || 
      event.name.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || event.event_status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  // Format date for display
  const formatEventDate = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const options = { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };

    if (start.toDateString() === end.toDateString()) {
      return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} • ${start.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })} - ${end.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`;
    } else {
      return `${start.toLocaleDateString('en-US', options)} - ${end.toLocaleDateString('en-US', options)}`;
    }
  };

  // Get status color and icon
  const getStatusDisplay = (eventStatus) => {
    switch (eventStatus) {
      case 'upcoming':
        return { color: 'blue', icon: Clock, text: 'Upcoming' };
      case 'active':
        return { color: 'green', icon: Activity, text: 'Live' };
      case 'past':
        return { color: 'gray', icon: CheckCircle, text: 'Completed' };
      default:
        return { color: 'gray', icon: Clock, text: 'Unknown' };
    }
  };

  if (loading) {
    return (
      <div className="curator-events">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading your events...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="curator-events">
        <div className="error-state">
          <p>{error}</p>
          <button onClick={loadCuratorEvents} className="btn-primary">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="curator-events">
      {/* Header */}
      <div className="events-header">
        <div className="header-content">
          <h1>My Events</h1>
          <p>Events from your galleries and collections</p>
        </div>
        <button onClick={loadCuratorEvents} className="btn-refresh">
          <RefreshCw className="refresh-icon" color="#6c757d" />
          Refresh
        </button>
      </div>

      {/* Stats Cards */}
      <div className="events-stats">
        <div className="stat-card">
          <Calendar className="stat-icon" color="#007bff" />
          <div className="stat-info">
            <span className="stat-label">Total Events</span>
            <span className="stat-value">{stats.total}</span>
          </div>
        </div>
        <div className="stat-card">
          <Clock className="stat-icon upcoming-icon" color="#fd7e14" />
          <div className="stat-info">
            <span className="stat-label">Upcoming</span>
            <span className="stat-value">{stats.upcoming}</span>
          </div>
        </div>
        <div className="stat-card">
          <CheckCircle className="stat-icon past-icon" color="#28a745" />
          <div className="stat-info">
            <span className="stat-label">Completed</span>
            <span className="stat-value">{stats.past}</span>
          </div>
        </div>
        <div className="stat-card">
          <Users className="stat-icon" color="#6f42c1" />
          <div className="stat-info">
            <span className="stat-label">Total Attendees</span>
            <span className="stat-value">{stats.attendees}</span>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="events-filters">
        <div className="search-box">
          <Search className="search-icon" color="#6c757d" />
          <input
            type="text"
            placeholder="Search events..."
            className="search-input"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        
        <div className="filter-box">
          <Filter className="filter-icon" color="#6c757d" />
          <select
            className="filter-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All Events</option>
            <option value="upcoming">Upcoming</option>
            <option value="active">Live Now</option>
            <option value="past">Completed</option>
          </select>
        </div>
      </div>

      {/* Events List */}
      <div className="events-container">
        {filteredEvents.length === 0 ? (
          <div className="empty-state">
                    <Calendar className="empty-icon" color="#6c757d" />
        <h3>No Events Found</h3>
            <p>No events match your current filters, or you don't have access to any events yet.</p>
          </div>
        ) : (
          filteredEvents.map(event => {
            const statusDisplay = getStatusDisplay(event.event_status);
            const StatusIcon = statusDisplay.icon;

            return (
              <div 
                key={event.id} 
                className="event-card"
                onClick={() => handleEventClick(event.id)}
              >
                <div className="event-header">
                  <div className="event-info">
                    <h3 className="event-name">{event.name}</h3>
                    <p className="event-date">{formatEventDate(event.event_start, event.event_end)}</p>
                  </div>
                  
                  <div className={`event-status ${statusDisplay.color}`}>
                    <StatusIcon className="status-icon" />
                    {statusDisplay.text}
                  </div>
                </div>
                
                <div className="event-metadata">
                  <div className="metadata-item">
                    <MapPin className="metadata-icon" color="#6c757d" />
                    <span>{event.gallery_name}</span>
                  </div>
                  
                  {event.collection_title && (
                    <div className="metadata-item">
                      <BookOpen className="metadata-icon" color="#28a745" />
                      <span>{event.collection_title}</span>
                    </div>
                  )}
                </div>
                
                <div className="event-stats">
                  <div className="stat-item">
                    <Users className="stat-icon" color="#6f42c1" />
                    <span>{event.attendee_count} attendees</span>
                    {event.checked_in_count > 0 && (
                      <span className="checked-in">({event.checked_in_count} checked in)</span>
                    )}
                  </div>
                  
                  <div className="stat-item">
                    <BookOpen className="stat-icon" color="#28a745" />
                    <span>{event.story_count} stories</span>
                    {event.featured_stories > 0 && (
                      <span className="featured">({event.featured_stories} featured)</span>
                    )}
                  </div>
                  
                  {event.eventbrite_id && (
                    <div className="stat-item">
                      <TrendingUp className="stat-icon" color="#fd7e14" />
                      <span>Eventbrite</span>
                    </div>
                  )}
                </div>
                
                <div className="event-footer">
                  <span className="event-id">Event #{event.id}</span>
                  <button 
                    className="event-view-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEventClick(event.id);
                    }}
                  >
                    <Eye className="btn-icon" color="#6c757d" />
                    View Details
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default CuratorEvents;
